import { Alert, Box, Button, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { useSession } from '@/auth';

/**
 * Gate for anything that needs an identity. Rather than redirecting immediately it
 * renders an explicit sign-in prompt, which keeps the unauthenticated state
 * observable — both for users returning to a stale tab and for the e2e suite.
 */
export const ProtectedRoute = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation();
  const session = useSession();

  if (session.isLoading) {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', p: 8 }}
        data-automation-id="session-loading"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!session.isAuthenticated) {
    return (
      <Paper sx={{ p: 4, maxWidth: 520, mx: 'auto', mt: 8 }} data-automation-id="signin-prompt">
        <Stack spacing={2}>
          <Typography variant="h2">{t('auth.SignInRequired')}</Typography>
          <Typography color="text.secondary">
            {session.mode === 'test-login' ? t('auth.TestLoginNotice') : t('auth.RedirectNotice')}
          </Typography>
          {session.error ? <Alert severity="error">{session.error}</Alert> : null}
          <Button variant="contained" onClick={session.signIn} data-automation-id="signin-button">
            {session.mode === 'test-login'
              ? t('auth.SignInAsTestUser')
              : t('auth.SignInWithTimeLog')}
          </Button>
        </Stack>
      </Paper>
    );
  }

  return <>{children}</>;
};
